import React, { useContext, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { User, LogOut, LayoutDashboard, Crown, Menu, BookOpen } from 'lucide-react';
import LanguageSwitcher from '@/components/LanguageSwitcher';

const Navbar = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav 
      data-testid="navbar"
      className={`navbar ${scrolled ? 'scrolled' : ''}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link 
            to="/" 
            data-testid="nav-logo"
            className="flex items-center space-x-3"
          >
            <img 
              src="/logo.png" 
              alt="Nazca360 Logo" 
              className="h-12 w-auto object-contain"
            />
            <span className="text-xl font-bold text-amber-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              Nazca360
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link 
              to="/gallery" 
              data-testid="nav-gallery"
              className="text-gray-700 hover:text-amber-900 font-medium"
            >
              {t('nav.gallery')}
            </Link>
            <Link
              to="/map3d"
              data-testid="nav-map3d"
              className="text-gray-700 hover:text-amber-900 font-medium flex items-center gap-1"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.5 3l-.16.03L15 5.1 9 3 3.36 4.9c-.21.07-.36.25-.36.48V20.5c0 .28.22.5.5.5l.16-.03L9 18.9l6 2.1 5.64-1.9c.21-.07.36-.25.36-.48V3.5c0-.28-.22-.5-.5-.5zM15 19l-6-2.11V5l6 2.11V19z"/>
              </svg>
              {t('nav.map3d')}
            </Link>
            <Link
              to="/suscripciones"
              data-testid="nav-subscriptions"
              className="text-gray-700 hover:text-amber-900 font-medium"
            >
              Suscripciones
            </Link>
            <Link
              to="/blog"
              data-testid="nav-blog"
              className="text-gray-700 hover:text-amber-900 font-medium flex items-center gap-1"
            >
              <BookOpen className="w-4 h-4" />
              {t('nav.blog')}
            </Link>
            <Link 
              to="/contact" 
              data-testid="nav-contact"
              className="text-gray-700 hover:text-amber-900 font-medium"
            >
              {t('nav.contact')}
            </Link>

            <LanguageSwitcher />

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    data-testid="user-menu-button"
                    variant="outline" 
                    className="rounded-full border-2 border-amber-700"
                  >
                    <User className="w-4 h-4 mr-2" />
                    {user.name}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem 
                    data-testid="menu-dashboard"
                    onClick={() => navigate('/dashboard')}
                  >
                    <LayoutDashboard className="mr-2 h-4 w-4" />
                    {t('nav.dashboard')}
                  </DropdownMenuItem>
                  {user.role === 'admin' && (
                    <DropdownMenuItem 
                      data-testid="menu-admin"
                      onClick={() => navigate('/admin')}
                    >
                      <Crown className="mr-2 h-4 w-4" />
                      {t('nav.admin')}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    data-testid="menu-logout"
                    onClick={logout}
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    {t('nav.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                data-testid="nav-login-button"
                onClick={() => navigate('/auth/login')}
                className="btn-peru rounded-full"
              >
                {t('nav.login')}
              </Button>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center gap-2">
            <LanguageSwitcher compact />
            <Button
              data-testid="mobile-menu-button"
              variant="ghost"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <Menu className="w-6 h-6" />
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 space-y-2">
            <Link
              to="/gallery"
              className="block px-4 py-2 text-gray-700 hover:bg-amber-50 rounded"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('nav.gallery')}
            </Link>
            <Link
              to="/map3d"
              className="block px-4 py-2 text-gray-700 hover:bg-amber-50 rounded flex items-center gap-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.5 3l-.16.03L15 5.1 9 3 3.36 4.9c-.21.07-.36.25-.36.48V20.5c0 .28.22.5.5.5l.16-.03L9 18.9l6 2.1 5.64-1.9c.21-.07.36-.25.36-.48V3.5c0-.28-.22-.5-.5-.5zM15 19l-6-2.11V5l6 2.11V19z"/>
              </svg>
              {t('nav.map3d')}
            </Link>
            <Link
              to="/suscripciones"
              className="block px-4 py-2 text-gray-700 hover:bg-amber-50 rounded"
              onClick={() => setMobileMenuOpen(false)}
            >
              Suscripciones
            </Link>
            <Link
              to="/blog"
              data-testid="mobile-nav-blog"
              className="block px-4 py-2 text-gray-700 hover:bg-amber-50 rounded flex items-center gap-2"
              onClick={() => setMobileMenuOpen(false)}
            >
              <BookOpen className="w-4 h-4" />
              {t('nav.blog')}
            </Link>
            <Link
              to="/contact"
              className="block px-4 py-2 text-gray-700 hover:bg-amber-50 rounded"
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('nav.contact')}
            </Link>
            {user ? (
              <>
                <Link
                  to="/dashboard"
                  className="block px-4 py-2 text-gray-700 hover:bg-amber-50 rounded"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t('nav.dashboard')}
                </Link>
                {user.role === 'admin' && (
                  <Link
                    to="/admin"
                    className="block px-4 py-2 text-gray-700 hover:bg-amber-50 rounded"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t('nav.admin')}
                  </Link>
                )}
                <button
                  onClick={() => {
                    logout();
                    setMobileMenuOpen(false);
                  }}
                  className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-amber-50 rounded"
                >
                  {t('nav.logout')}
                </button>
              </>
            ) : (
              <button
                onClick={() => {
                  navigate('/auth/login');
                  setMobileMenuOpen(false);
                }}
                className="block w-full text-left px-4 py-2 bg-gradient-to-r from-amber-600 to-orange-700 text-white rounded font-medium"
              >
                {t('nav.login')}
              </button>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;